import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { DIET_OPTIONS } from '../constants/profile';
import Icon from './Icon';
import Toast from './Toast';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/recipes';

interface Member {
  id: number;
  username: string;
}

interface HouseholdRow {
  id: number;
  name: string;
  diet_preference: string;
  members: Member[];
  created_at: string;
}

const emptyNewHousehold = { name: '', diet_preference: 'none' };
const emptyNewUser = { username: '', password: '' };

const HouseholdAdmin: React.FC = () => {
  const queryClient = useQueryClient();
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showNewHousehold, setShowNewHousehold] = useState(false);
  const [newHousehold, setNewHousehold] = useState(emptyNewHousehold);
  const [addingUserTo, setAddingUserTo] = useState<HouseholdRow | null>(null);
  const [newUser, setNewUser] = useState(emptyNewUser);
  const [editingHousehold, setEditingHousehold] = useState<HouseholdRow | null>(null);
  const [editHouseholdForm, setEditHouseholdForm] = useState(emptyNewHousehold);
  const [deletingHousehold, setDeletingHousehold] = useState<HouseholdRow | null>(null);
  const [removingMember, setRemovingMember] = useState<{ household: HouseholdRow; member: Member } | null>(null);
  const [resettingPasswordFor, setResettingPasswordFor] = useState<{ household: HouseholdRow; member: Member } | null>(null);
  const [newPassword, setNewPassword] = useState('');

  const errorDetail = (error: unknown): string | null =>
    axios.isAxiosError(error) ? error.response?.data?.error ?? null : null;

  const { data: households, isLoading, isError } = useQuery({
    queryKey: ['admin-households'],
    queryFn: async () => {
      const response = await axios.get(`${API_URL}/admin/households/`);
      return response.data as HouseholdRow[];
    },
  });

  const createHouseholdMutation = useMutation({
    mutationFn: async (payload: typeof emptyNewHousehold) => {
      const response = await axios.post(`${API_URL}/admin/households/`, payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-households'] });
      setShowNewHousehold(false);
      setNewHousehold(emptyNewHousehold);
      setMessage({ type: 'success', text: 'Household created.' });
    },
    onError: (error) => {
      setMessage({ type: 'error', text: errorDetail(error) || 'Error creating household.' });
    },
  });

  const editHouseholdMutation = useMutation({
    mutationFn: async ({ householdId, payload }: { householdId: number; payload: typeof emptyNewHousehold }) => {
      const response = await axios.patch(`${API_URL}/admin/households/${householdId}/`, payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-households'] });
      setEditingHousehold(null);
      setMessage({ type: 'success', text: 'Household updated.' });
    },
    onError: (error) => {
      setMessage({ type: 'error', text: errorDetail(error) || 'Error updating household.' });
    },
  });

  const deleteHouseholdMutation = useMutation({
    mutationFn: async (householdId: number) => {
      await axios.delete(`${API_URL}/admin/households/${householdId}/`);
    },
    onSuccess: (_data, householdId) => {
      queryClient.invalidateQueries({ queryKey: ['admin-households'] });
      setDeletingHousehold(null);
      setMessage({ type: 'success', text: 'Household and its logins were deleted.' });
      void householdId;
    },
    onError: (error) => {
      setMessage({ type: 'error', text: errorDetail(error) || 'Error deleting household.' });
    },
  });

  const addUserMutation = useMutation({
    mutationFn: async ({ householdId, payload }: { householdId: number; payload: typeof emptyNewUser }) => {
      const response = await axios.post(`${API_URL}/admin/households/${householdId}/users/`, payload);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['admin-households'] });
      setAddingUserTo(null);
      setNewUser(emptyNewUser);
      setMessage({ type: 'success', text: `Login "${data.username}" created.` });
    },
    onError: (error) => {
      setMessage({ type: 'error', text: errorDetail(error) || 'Error creating login.' });
    },
  });

  const removeMemberMutation = useMutation({
    mutationFn: async (userId: number) => {
      await axios.delete(`${API_URL}/admin/users/${userId}/`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-households'] });
      setRemovingMember(null);
      setMessage({ type: 'success', text: 'Login removed.' });
    },
    onError: (error) => {
      setMessage({ type: 'error', text: errorDetail(error) || 'Error removing login.' });
    },
  });

  const resetPasswordMutation = useMutation({
    mutationFn: async ({ userId, password }: { userId: number; password: string }) => {
      const response = await axios.post(`${API_URL}/admin/users/${userId}/reset-password/`, { password });
      return response.data;
    },
    onSuccess: (data) => {
      setResettingPasswordFor(null);
      setNewPassword('');
      setMessage({ type: 'success', text: `Password reset for "${data.username}".` });
    },
    onError: (error) => {
      setMessage({ type: 'error', text: errorDetail(error) || 'Error resetting password.' });
    },
  });

  const handleCreateHousehold = (e: React.FormEvent) => {
    e.preventDefault();
    if (newHousehold.name.trim()) {
      createHouseholdMutation.mutate(newHousehold);
    }
  };

  const openEditHousehold = (h: HouseholdRow) => {
    setEditingHousehold(h);
    setEditHouseholdForm({ name: h.name, diet_preference: h.diet_preference });
  };

  const handleEditHousehold = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingHousehold && editHouseholdForm.name.trim()) {
      editHouseholdMutation.mutate({ householdId: editingHousehold.id, payload: editHouseholdForm });
    }
  };

  const handleAddUser = (e: React.FormEvent) => {
    e.preventDefault();
    if (addingUserTo && newUser.username.trim() && newUser.password) {
      addUserMutation.mutate({ householdId: addingUserTo.id, payload: newUser });
    }
  };

  const handleResetPassword = (e: React.FormEvent) => {
    e.preventDefault();
    if (resettingPasswordFor && newPassword) {
      resetPasswordMutation.mutate({ userId: resettingPasswordFor.member.id, password: newPassword });
    }
  };

  const dietLabel = (value: string) => DIET_OPTIONS.find((d) => d.value === value)?.label || value;

  if (isLoading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner" />
        <p>Loading households...</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="empty-state">
        <Icon name="warning" className="icon empty-icon" />
        <h2>Superuser Access Required</h2>
        <p>This page is only available to the platform administrator account.</p>
      </div>
    );
  }

  return (
    <div className="household-admin">
      <div className="dashboard-header">
        <h1><Icon name="box" className="icon" />Household Admin</h1>
        <p>Provision, edit, and remove households and their logins. Only visible to the platform administrator.</p>
      </div>

      <button onClick={() => setShowNewHousehold(true)} className="btn-primary" style={{ marginBottom: '1.5rem' }}>
        <Icon name="sparkle" className="icon icon-sm" />
        New Household
      </button>

      <div className="household-admin-list">
        {(households || []).map((h) => (
          <div key={h.id} className="settings-card">
            <div className="household-admin-card-header">
              <Icon name="box" className="icon card-icon" />
              <div className="household-admin-card-actions">
                <button
                  onClick={() => openEditHousehold(h)}
                  className="icon-btn"
                  aria-label={`Edit ${h.name}`}
                >
                  <Icon name="edit" className="icon icon-sm" />
                </button>
                <button
                  onClick={() => setDeletingHousehold(h)}
                  className="icon-btn"
                  aria-label={`Delete ${h.name}`}
                >
                  <Icon name="trash" className="icon icon-sm" />
                </button>
              </div>
            </div>
            <h2>{h.name}</h2>
            <div className="info-grid">
              <div className="info-item">
                <span className="info-label">Diet:</span>
                <span className="info-value">{dietLabel(h.diet_preference)}</span>
              </div>
            </div>

            <div className="household-admin-members">
              <span className="info-label">Members:</span>
              {h.members.length === 0 ? (
                <p className="scan-review-hint">None yet</p>
              ) : (
                <ul className="household-admin-member-list">
                  {h.members.map((m) => (
                    <li key={m.id}>
                      <span>{m.username}</span>
                      <div className="household-admin-member-actions">
                        <button
                          onClick={() => { setResettingPasswordFor({ household: h, member: m }); setNewPassword(''); }}
                          className="btn-secondary"
                        >
                          Reset Password
                        </button>
                        <button
                          onClick={() => setRemovingMember({ household: h, member: m })}
                          className="btn-danger"
                        >
                          Remove
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <button
              onClick={() => { setAddingUserTo(h); setNewUser(emptyNewUser); }}
              className="btn-secondary settings-card-save"
            >
              <Icon name="edit" className="icon icon-sm" />
              Add Login
            </button>
          </div>
        ))}
      </div>

      {message && (
        <Toast type={message.type} text={message.text} onDismiss={() => setMessage(null)} />
      )}

      {showNewHousehold && (
        <div className="modal-overlay" onClick={() => setShowNewHousehold(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowNewHousehold(false)}>✕</button>
            <h2>New Household</h2>
            <form onSubmit={handleCreateHousehold} className="add-item-form">
              <div className="form-group">
                <label>Household Name *</label>
                <input
                  type="text"
                  value={newHousehold.name}
                  onChange={(e) => setNewHousehold({ ...newHousehold, name: e.target.value })}
                  placeholder="e.g., The Garcia Family"
                  required
                />
              </div>
              <div className="form-group">
                <label>Diet Preference</label>
                <select
                  value={newHousehold.diet_preference}
                  onChange={(e) => setNewHousehold({ ...newHousehold, diet_preference: e.target.value })}
                >
                  {DIET_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <p className="scan-review-hint">
                Allergies/restrictions can be set afterward from that household's own Settings page.
              </p>
              <div className="form-actions">
                <button type="button" onClick={() => setShowNewHousehold(false)} className="btn-secondary">
                  Cancel
                </button>
                <button type="submit" disabled={createHouseholdMutation.isPending} className="btn-primary">
                  {createHouseholdMutation.isPending ? 'Creating...' : 'Create Household'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {editingHousehold && (
        <div className="modal-overlay" onClick={() => setEditingHousehold(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setEditingHousehold(null)}>✕</button>
            <h2>Edit {editingHousehold.name}</h2>
            <form onSubmit={handleEditHousehold} className="add-item-form">
              <div className="form-group">
                <label>Household Name *</label>
                <input
                  type="text"
                  value={editHouseholdForm.name}
                  onChange={(e) => setEditHouseholdForm({ ...editHouseholdForm, name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Diet Preference</label>
                <select
                  value={editHouseholdForm.diet_preference}
                  onChange={(e) => setEditHouseholdForm({ ...editHouseholdForm, diet_preference: e.target.value })}
                >
                  {DIET_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div className="form-actions">
                <button type="button" onClick={() => setEditingHousehold(null)} className="btn-secondary">
                  Cancel
                </button>
                <button type="submit" disabled={editHouseholdMutation.isPending} className="btn-primary">
                  {editHouseholdMutation.isPending ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deletingHousehold && (
        <div className="modal-overlay" onClick={() => setDeletingHousehold(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setDeletingHousehold(null)}>✕</button>
            <h2><Icon name="warning" className="icon" />Delete {deletingHousehold.name}?</h2>
            <p>
              This permanently deletes the household along with its pantry, recipes, meal plan, and shopping
              list{deletingHousehold.members.length > 0
                ? `, and removes ${deletingHousehold.members.length} login${deletingHousehold.members.length === 1 ? '' : 's'} (${deletingHousehold.members.map((m) => m.username).join(', ')})`
                : ''}. This can't be undone.
            </p>
            <div className="form-actions">
              <button type="button" onClick={() => setDeletingHousehold(null)} className="btn-secondary">
                Cancel
              </button>
              <button
                type="button"
                onClick={() => deleteHouseholdMutation.mutate(deletingHousehold.id)}
                disabled={deleteHouseholdMutation.isPending}
                className="btn-danger"
              >
                {deleteHouseholdMutation.isPending ? 'Deleting...' : 'Yes, Delete Household'}
              </button>
            </div>
          </div>
        </div>
      )}

      {addingUserTo && (
        <div className="modal-overlay" onClick={() => setAddingUserTo(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setAddingUserTo(null)}>✕</button>
            <h2>Add Login for {addingUserTo.name}</h2>
            <form onSubmit={handleAddUser} className="add-item-form">
              <div className="form-group">
                <label>Username *</label>
                <input
                  type="text"
                  value={newUser.username}
                  onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Password *</label>
                <input
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  placeholder="At least 8 characters"
                  required
                />
              </div>
              <div className="form-actions">
                <button type="button" onClick={() => setAddingUserTo(null)} className="btn-secondary">
                  Cancel
                </button>
                <button type="submit" disabled={addUserMutation.isPending} className="btn-primary">
                  {addUserMutation.isPending ? 'Creating...' : 'Create Login'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {resettingPasswordFor && (
        <div className="modal-overlay" onClick={() => setResettingPasswordFor(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setResettingPasswordFor(null)}>✕</button>
            <h2>Reset Password for {resettingPasswordFor.member.username}</h2>
            <form onSubmit={handleResetPassword} className="add-item-form">
              <div className="form-group">
                <label>New Password *</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  autoFocus
                  required
                />
              </div>
              <div className="form-actions">
                <button type="button" onClick={() => setResettingPasswordFor(null)} className="btn-secondary">
                  Cancel
                </button>
                <button type="submit" disabled={resetPasswordMutation.isPending} className="btn-primary">
                  {resetPasswordMutation.isPending ? 'Saving...' : 'Reset Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {removingMember && (
        <div className="modal-overlay" onClick={() => setRemovingMember(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setRemovingMember(null)}>✕</button>
            <h2><Icon name="warning" className="icon" />Remove {removingMember.member.username}?</h2>
            <p>
              This permanently deletes the login "{removingMember.member.username}" from {removingMember.household.name}.
              The household itself and its data are unaffected.
            </p>
            <div className="form-actions">
              <button type="button" onClick={() => setRemovingMember(null)} className="btn-secondary">
                Cancel
              </button>
              <button
                type="button"
                onClick={() => removeMemberMutation.mutate(removingMember.member.id)}
                disabled={removeMemberMutation.isPending}
                className="btn-danger"
              >
                {removeMemberMutation.isPending ? 'Removing...' : 'Yes, Remove Login'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HouseholdAdmin;
